/* ••[1]••••••••••••••••••••••••• EventCalendarListController.ts •••••••••••••••••••••••••••••• */

/* ••[2]•••••••••• Components ••••••••••••••• */
import "../components/event-list/index";
import { CCCEventCalendar } from "../components/calendar/index";
import { CCCEventList } from "../components/event-list/index";
import { CCCSelectSingle } from "../components/forms/select-single";
import { CCCPanel } from "../components/panel/panel";

/* ••[2]•••••••••• Interfaces ••••••••••••••• */
import { EventSourceError, FetchInfo } from "../core/interfaces/FullCalendar";
import Event, { EventPaginated, EventRSVP } from "../core/interfaces/Event";
import SelectItem from "../core/interfaces/SelectItem";
import { PageDirection, SortOption } from "../core/interfaces/Pagination";
import { VanillaRSVP } from "../core/interfaces/VanillaApi";

/* ••[2]•••••••••• Models ••••••••••••••• */
import EventModel from "../models/EventModel";
import OContentModel from "../models/OContentModel";

export default class EventCalendarListController {
  private $calendar: CCCEventCalendar = null;
  private $eventList: CCCEventList = null;
  private $panel: CCCPanel;
  private thumbnailCache: { [eventId: string]: HTMLImageElement } = {};

  constructor(calendarAnchor: string, listAnchor: string, panelAnchor: string) {
    try {
      this.$calendar = document.querySelector(calendarAnchor);
      this.$panel = document.querySelector(panelAnchor);
      this.$eventList = document.querySelector(listAnchor);
      if (!this.$calendar || !this.$panel || !this.$eventList) {
        throw `Event Calendar List - ${calendarAnchor}, ${panelAnchor} or ${listAnchor} not found in DOM`;
      }

      this.$panel.style.display = "none";
    } catch (error) {
      console.error(error);
    }
  }

  public async init(): Promise<void> {
    if (this.$panel && this.$eventList) {
      if (await EventModel.isEventsCategoryPage()) {
        this.$panel.style.display = "block";
        this.$eventList.display(true);
        this.setupComponents();
      } else {
        this.$eventList.remove();
        this.$panel.remove();
      }
    }
  }

  private setupComponents(): void {
    this.setupCalendar();
    this.setupEventList();
    this.$calendar.init();
    this.$eventList.init();
  }

  private setupCalendar(): void {
    this.$calendar.onFetchEvents(
      (
        fetchInfo: FetchInfo,
        successCallback: (events: Array<Event>) => void,
        failureCallback: (error: EventSourceError) => void,
        currentDate: Date
      ) => {
        this.$calendar.setLoading(true);
        setTimeout(() => {
          const dateStart: string = fetchInfo.startStr.slice(0, 10);
          const dateEnd: string = fetchInfo.endStr.slice(0, 10);
          EventModel.getEventsCached(
            dateStart,
            dateEnd,
            currentDate.getFullYear().toString() +
              "-" +
              currentDate.getMonth().toString(),
            SortOption.asc,
            500,
            1
          )
            .then((result: EventPaginated): void => {
              successCallback(result.events);
              this.$calendar.setLoading(false);
              this.$calendar.render();
            })
            .catch((error) => {
              failureCallback(error);
            });
        }, 200); // fake 200ms loading time
      }
    );
    this.$calendar.onDateSelected((date: string) => {
      if (this.$eventList) {
        this.$eventList.filterByDate(date);
      }
    });
  }

  private setupEventList(): void {
    this.$eventList.onFetchEvents(
      (
        startDate: string,
        endDate: string,
        myEvents: boolean,
        sort = SortOption.asc,
        pageSize = -1,
        page = -1
      ) => {
        this.$eventList.setLoading(true);
        setTimeout(() => {
          EventModel.getEventsCached(
            startDate,
            endDate,
            startDate + "-" + endDate + (page > 0 ? "-" + page.toString() : ""),
            sort,
            pageSize,
            page
          )
            .then((result: Array<Event> | EventPaginated) => {
              this.$eventList.setLoading(false);
              if ("pagination" in result) {
                const nextPage = result.pagination.has(PageDirection.next)
                  ? Number(result.pagination.get(PageDirection.next).page)
                  : page + 1;
                if (this.$eventList.getCurrentPage() !== nextPage - 1) {
                  this.$eventList.hasMore(result.events);
                } else {
                  this.$eventList.setPagination(result.pagination);
                  this.$eventList.setEvents(result.events, myEvents);
                  this.setUpVideoThumbnails(result.events);
                }
              } else {
                this.$eventList.setEvents(result, myEvents);
                this.setUpVideoThumbnails(result);
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }, 200); // fake 200ms loading time
      }
    );
    this.$eventList.onRSVP(
      (eventID: number, select: CCCSelectSingle, value: SelectItem) => {
        select.setLoading(true);
        let rsvpOption: string;
        switch (value.name) {
          case EventRSVP.Going:
            rsvpOption = VanillaRSVP.Going;
            break;
          case EventRSVP.NotGoing:
            rsvpOption = VanillaRSVP.NotGoing;
            break;
          case EventRSVP.Maybe:
            rsvpOption = VanillaRSVP.Maybe;
            break;
          default:
            rsvpOption = value.name;
            break;
        }
        EventModel.rsvpEvent(rsvpOption, eventID)
          .then((success) => {
            if (!success) {
              select.displayPlaceholder();
            }
            select.setLoading(false);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    );
  }

  private async setUpVideoThumbnails(events: Array<Event>) {
    const processedEvents = new Set<number>();

    for (const event of events.filter(
      (e) => new Date(e.dateEnd) < new Date()
    )) {
      const eventList = document.querySelector(`#EventList`);
      const eventContainer = eventList?.shadowRoot.querySelector(
        `#event-${event.eventID}`
      );

      if (!eventContainer || processedEvents.has(event.eventID)) continue;

      processedEvents.add(event.eventID);

      if (this.thumbnailCache[event.eventID]) {
        console.debug(`Using cached thumbnail for event ${event.eventID}`);
        if (!eventContainer.querySelector(".event-thumbnail")) {
          eventContainer.appendChild(
            this.thumbnailCache[event.eventID].cloneNode(true)
          );
        }
        continue;
      }

      const videoString = await OContentModel.getEventVideoReplay(
        event.eventID.toString()
      );
      const entryId =
        videoString?.match(/entry_id=([a-zA-Z0-9_-]+)/)?.[1] ||
        "default_entry_id";
      const thumbnailUrl = videoString
        ? `https://cfvod.kaltura.com/p/2171811/sp/217181100/thumbnail/entry_id/${entryId}/height/500/width/622`
        : `https://www.oracle.com/webfolder/community/ccc/prod/images/custom-pages/video-coming-soon.png`;

      if (!eventContainer.querySelector(".event-thumbnail")) {
        const imgElement = Object.assign(document.createElement("img"), {
          src: thumbnailUrl,
          alt: `Thumbnail for ${event.eventID}`,
          className: "event-thumbnail",
          style: "border-radius: 6px; align-self: center;",
        });
        eventContainer.appendChild(imgElement);
        this.thumbnailCache[event.eventID] = imgElement.cloneNode(
          true
        ) as HTMLImageElement;
      } else {
        console.debug(`Thumbnail already appended for event ${event.eventID}`);
      }
    }
  }
}
